import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import getProductType from '../helpers/getProductType';
import useDebouncedValue from './useDebouncedValue';
import { PRODUCT_TYPES } from '../utils/constants';
import backend from '../api/server';
import {
  extractScheduledDate,
  formatCurrencyWithoutSymbol,
  removeScheduledDate,
} from '../helpers/order';

const useOrderDetails = (orderId = 0) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [orderState, setOrderState] = useState({
    order: null,
    cart: null,
    originalCart: null,
    editedItems: {},
    shippingMethod: '',
    loading: true,
    updating: false,
    isProcessing: false,
    editing: false,
    searchValue: '',
    searchedProducts: [],
    selectedProduct: null,
    paymentDetails: null,
    coupons: [],
    couponsFallbackUsed: false,
    recalculatedTotal: null,
    recalcDiscountTotal: null,
    recalcLines: [],
    recalcWarnings: [],
    recalculating: false,
  });
  const [modals, setModals] = useState({
    confirm: false,
    addProduct: false,
    confirmPickup: false,
    confirmAndPayLater: false,
  });
  const debouncedSearchTerm = useDebouncedValue(orderState.searchValue, 1000);

  useEffect(() => {
    const fetchData = async () => {
      setOrderState((prevState) => ({ ...prevState, loading: true }));
      try {
        console.count('fetchData called');

        // Fetch order details
        const orderResponse = await backend.get(`/orders/${orderId}`);
        const orderData = orderResponse.data;

        // Fetch cart if cart_id is available
        let cartData = null, paymentDetails = null;
        if (orderData.cart_id) {
          const cartResponse = await backend.get(`/cart/${orderData.cart_id}`);
          cartData = cartResponse.data;
        }

        if (orderData.payment_method !== 'Pago Contra Entrega Envío' && orderData.payment_method !== 'Pago Contra Entrega Sucursal') {
          // Fetch payment details
          const paymentData = await backend.post(`/orders/${orderId}/payment-method`, { customerId: orderData.customer_id, cart_id: orderData.cart_id });
          paymentDetails = paymentData.data;
        }

        // Fetch coupons (fuente de verdad: backend Fastify, ya que BC los borra al editar)
        let coupons = [];
        let couponsFallbackUsed = false;
        try {
          const couponsResponse = await backend.get(`/orders/${orderId}/coupons`);
          coupons = Array.isArray(couponsResponse.data?.coupons)
            ? couponsResponse.data.coupons
            : [];
          couponsFallbackUsed = !!couponsResponse.data?.fallbackUsed;
        } catch (couponsErr) {
          console.warn('Could not fetch coupons for order:', couponsErr.message);
        }

        // Update all states at once
        setOrderState((prevState) => ({
          ...prevState,
          paymentDetails,
          order: orderData,
          cart: cartData,
          originalCart: cartData,
          coupons,
          couponsFallbackUsed,
          recalculatedTotal: null,
          recalcDiscountTotal: null,
          recalcLines: [],
          recalcWarnings: [],
          shippingMethod:
            orderData.consignments &&
            orderData.consignments.shipping &&
            orderData.consignments.shipping.length > 0
              ? orderData.consignments.shipping[0].shipping_method
              : '',
          loading: false,
        }));
      } catch (error) {
        console.error('Error fetching data:', error);
        setOrderState((prevState) => ({
          ...prevState,
          loading: false,
        }));
      }
    };

    fetchData();
  }, [orderId, orderState.updating]);

  useEffect(() => {
    const searchTerm = async () => {
      try {
        const response = await backend.post(`/search`, {
          term: debouncedSearchTerm,
        });
        setOrderState((prevState) => ({
          ...prevState,
          searchedProducts: response?.data?.products || [],
        }));
      } catch (error) {
        console.error('Error searching product:', error);
        setOrderState((prevState) => ({
          ...prevState,
          searchedProducts: [],
        }));
      }
    };

    if (orderState.searchValue !== '') {
      searchTerm();
    } else {
      setOrderState((prevState) => ({
        ...prevState,
        searchedProducts: [],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  // Auto-fetch del breakdown de cupones en background.
  // BC v2 /orders/:id/coupons solo reporta cupones aplicados realmente al
  // crear la orden vía storefront. En casos donde el monto no viene de ahí
  // (testing manual, fallo de BC, etc.), pedimos al Fastify que calcule el
  // breakdown vía /recalculate y rellenamos el monto. Es silencioso: no
  // toca `recalculatedTotal` ni habilita el botón Cobrar.
  const breakdownFetchedRef = useRef(new Set());

  useEffect(() => {
    breakdownFetchedRef.current = new Set();
  }, [orderId]);

  // Cuando el operador inicia una edición (isDirty pasa a true), los montos
  // cacheados del breakdown ya no son válidos (los items pueden cambiar).
  // Limpiamos el ref para que tras el siguiente recálculo + refetch, el
  // auto-fetch del breakdown corra de nuevo si hace falta.
  const prevDirtyRef = useRef(false);
  useEffect(() => {
    const isDirtyNow = Object.keys(orderState.editedItems).length > 0;
    if (isDirtyNow && !prevDirtyRef.current) {
      breakdownFetchedRef.current = new Set();
    }
    prevDirtyRef.current = isDirtyNow;
  }, [orderState.editedItems]);

  useEffect(() => {
    const coupons = orderState.coupons || [];
    if (coupons.length === 0) return;
    if (breakdownFetchedRef.current.has(orderId)) return;
    if (!orderState.order?.products?.length) return;

    const someMissingAmount = coupons.some(
      (c) =>
        c.source === 'v3' &&
        (c.discount_amount === null || c.discount_amount === undefined),
    );
    if (!someMissingAmount) return;

    breakdownFetchedRef.current.add(orderId);

    const items = orderState.order.products
      .filter((p) => Number(p.quantity) > 0)
      .map((p) => ({
        product_id: p.product_id,
        variant_id: p.variant_id > 0 ? p.variant_id : undefined,
        quantity: p.quantity,
        line_total: parseFloat(p.total_inc_tax),
      }));
    if (items.length === 0) return;
    const shippingTotal =
      parseFloat(orderState.order.shipping_cost_inc_tax) || 0;

    backend
      .post(`/orders/${orderId}/recalculate`, {
        items,
        shipping_total: shippingTotal,
      })
      .then(({ data }) => {
        if (!data || data.blocked) return;
        if (!Array.isArray(data.coupons_breakdown)) return;

        setOrderState((prev) => ({
          ...prev,
          coupons: (prev.coupons || []).map((c) => {
            const match = data.coupons_breakdown.find(
              (b) =>
                (b.coupon_id != null && b.coupon_id === c.coupon_id) ||
                (!!b.code && !!c.code && b.code === c.code),
            );
            if (
              match &&
              (c.discount_amount === null || c.discount_amount === undefined)
            ) {
              return { ...c, discount_amount: match.discount_amount };
            }
            return c;
          }),
        }));
      })
      .catch(() => {
        // Silencioso — el monto simplemente no aparece, no rompe la UI.
      });
  }, [orderState.coupons, orderState.order, orderId]);

  const canConfirmPickup = useMemo(() => {
    return !!(
      orderState.order &&
      (orderState.order.payment_method
        .toLowerCase()
        .includes('pago contra entrega') ||
        orderState.order.payment_method
          .toLowerCase()
          .includes('efectivo contra entrega')) &&
      orderState.shippingMethod.toLowerCase().includes('recoger') &&
      location.state?.from === 'preparando pedido' &&
      orderState.order.status_id === 9
    );
  }, [orderState.order, orderState.shippingMethod, location.state?.from]);

  const canConfirmSendAndPayLater = useMemo(() => {
    return !!(
      orderState.order &&
      (orderState.order.payment_method
        .toLowerCase()
        .includes('pago contra entrega') ||
        orderState.order.payment_method
          .toLowerCase()
          .includes('efectivo contra entrega')) &&
      (orderState.shippingMethod === 'Envío a domicilio' ||
        orderState.shippingMethod === 'Free Shipping') &&
      location.state?.from === 'preparando pedido' &&
      orderState.order.status_id === 9
    );
  }, [orderState.order, orderState.shippingMethod, location.state?.from]);

  const isDirty = useMemo(
    () => Object.keys(orderState.editedItems).length > 0,
    [orderState.editedItems]
  );

  const hasCoupons = useMemo(
    () => Array.isArray(orderState.coupons) && orderState.coupons.length > 0,
    [orderState.coupons]
  );

  // El recálculo y su gate del botón Cobrar SOLO aplican en "preparando pedido".
  // En las demás vistas los cupones se muestran como información histórica.
  const isPreparingOrder = useMemo(
    () => location.state?.from === 'preparando pedido',
    [location.state]
  );

  // Una orden ya cobrada se detecta por:
  //   - staff_notes con "Stripe Transaction ID" (lo escribe captureFunds tras
  //     Stripe charge exitoso); o
  //   - status_id === 11 (estado "Pagado / cobrado").
  // Si el cobro falla, ninguna de las dos condiciones se cumple, así que el
  // operador puede reintentar (botón Recalcular sigue visible).
  const alreadyCharged = useMemo(() => {
    if (!orderState.order) return false;
    const hasStripeNote =
      orderState.order.staff_notes &&
      orderState.order.staff_notes.includes('Stripe Transaction ID');
    return !!(hasStripeNote || orderState.order.status_id === 11);
  }, [orderState.order]);

  const requiresRecalculation = useMemo(
    () =>
      isPreparingOrder &&
      hasCoupons &&
      (isDirty || orderState.recalculatedTotal === null),
    [isPreparingOrder, hasCoupons, isDirty, orderState.recalculatedTotal]
  );

  const canCaptureFunds = useMemo(() => {
    const baseEligible = !!(
      orderState.order &&
      location.state?.from === 'preparando pedido' &&
      (!orderState.order.staff_notes ||
        !orderState.order.staff_notes.includes('Stripe Transaction ID')) &&
      !(
        orderState.order.payment_method
          .toLowerCase()
          .includes('pago contra entrega') ||
        orderState.order.payment_method
          .toLowerCase()
          .includes('efectivo contra entrega')
      ) &&
      orderState.order.status_id !== 11
    );

    if (!baseEligible) return false;
    // Cambios sin guardar: BC todavía no recibió la edición, cobraríamos el
    // total viejo. Forzar Guardar (o Recalcular cuando hay cupones, que internamente guarda).
    if (isDirty) return false;
    // Si hay cupones, el botón Cobrar sólo se habilita tras un recálculo limpio.
    if (hasCoupons) return orderState.recalculatedTotal !== null;
    return true;
  }, [
    orderState.order,
    location.state,
    hasCoupons,
    isDirty,
    orderState.recalculatedTotal,
  ]);

  // Alerta para el caso "editado sin guardar pero sin cupones".
  // Si hay cupones, la alerta de "Recalcular" ya cubre el caso (ese flow hace save+recalc).
  // No tiene sentido pedir guardar antes de cobrar si la orden ya fue cobrada.
  const requiresSaveBeforeCharge = useMemo(
    () => isPreparingOrder && isDirty && !hasCoupons && !alreadyCharged,
    [isPreparingOrder, isDirty, hasCoupons, alreadyCharged]
  );

  // Solo exponemos `hasCoupons` para los flows de cobro/recálculo cuando estamos
  // en preparando pedido Y la orden no ha sido cobrada todavía. En vistas
  // informativas (o tras cobro exitoso) pasamos `false` para que la UI no
  // muestre el botón Recalcular ni la alerta amarilla — la lista de cupones
  // se sigue mostrando aparte como información histórica.
  const hasCouponsForActions =
    hasCoupons && isPreparingOrder && !alreadyCharged;

  const openAddProductModal = () =>
    setModals((prevState) => ({ ...prevState, addProduct: true }));
  const openConfirmModal = () =>
    setModals((prevState) => ({ ...prevState, confirm: true }));
  const openConfirmPickupModal = () =>
    setModals((prevState) => ({ ...prevState, confirmPickup: true }));
  const openConfirmAndPayLaterModal = () =>
    setModals((prevState) => ({ ...prevState, confirmAndPayLater: true }));
  const closeAddProductModal = () =>
    setModals((prevState) => ({ ...prevState, addProduct: false }));
  const closeConfirmModal = () =>
    setModals((prevState) => ({ ...prevState, confirm: false }));
  const closeConfirmPickupModal = () =>
    setModals((prevState) => ({ ...prevState, confirmPickup: false }));
  const closeConfirmAndPayLaterModal = () =>
    setModals((prevState) => ({ ...prevState, confirmAndPayLater: false }));

  const handleQuantityValue = ({ target }) => {
    const val = +target.value;

    if (isNaN(val)) return;

    setOrderState((prevState) => ({
      ...prevState,
      selectedProduct: {
        ...prevState.selectedProduct,
        quantity: val,
      },
    }));
  };
  const handleSelectedWeightValue = ({ target }) => {
    const val = +target.value;

    if (isNaN(val)) return;

    setOrderState((prevState) => ({
      ...prevState,
      selectedProduct: {
        ...prevState.selectedProduct,
        selected_weight: val,
      },
    }));
  };

  const handleSearchValue = (e) =>
    setOrderState((prevState) => ({
      ...prevState,
      searchValue: e.target.value,
    }));

  const fetchOrderDetails = async () => {
    setOrderState((prevState) => ({ ...prevState, loading: true }));
    try {
      const response = await backend.get(`/orders/${orderId}`);
      const orderData = response.data;

      if (orderData.cart_id && !orderState.cart) {
        await fetchCart(orderData.cart_id);
      }

      setOrderState((prevState) => ({
        ...prevState,
        order: orderData,
        loading: false,
      }));
    } catch (error) {
      console.error('Error fetching order details:', error);
      setOrderState((prevState) => ({ ...prevState, loading: false }));
    } finally {
      setModals({
        confirm: false,
        addProduct: false,
        confirmPickup: false,
        confirmAndPayLater: false,
      });
      setOrderState((prevState) => ({
        ...prevState,
        searchValue: '',
        searchedProducts: [],
        selectedProduct: null,
      }));
    }
  };

  const fetchCart = async (cartId) => {
    if (!orderState.originalCart) {
      try {
        const response = await backend.get(`/cart/${cartId}`);
        setOrderState((prevState) => ({
          ...prevState,
          cart: response.data,
          originalCart: response.data,
        }));
      } catch (error) {
        console.error('Error fetching cart details:', error);
      }
    }
  };

  const handleQuantityChange = (productId, value) => {
    const item = orderState.cart.cartItems.find(
      (item) => item.product_id === productId
    );

    if (item) {
      const {
        cart_id,
        cart_item_id,
        product_id,
        custom_fields,
        name,
        sale_price,
        non_sale_price,
        quantity,
      } = item;
      const isKg = custom_fields && custom_fields.tipo === 'Kg';
      const isPieceKg = isKg && !!custom_fields.pesokg;
      const newValue = parseFloat(value) || 0;
      const price = sale_price || non_sale_price;

      const updatedItem = {
        product_id,
        quantity,
        cart_id,
        cart_item_id,
        custom_fields,
        name,
      };
      let visualTotal = 0;

      if (isPieceKg) {
        updatedItem.selected_weight = newValue;
        visualTotal = newValue * price;
      } else {
        updatedItem.quantity = isKg ? +newValue : Math.round(newValue);
        visualTotal = isKg ? +newValue * price : Math.round(newValue) * price;
      }

      setOrderState((prevState) => {
        const updatedCartItems = prevState.cart.cartItems.map((cartItem) =>
          cartItem.product_id === productId
            ? { ...cartItem, price: visualTotal }
            : cartItem
        );

        const newTotal = updatedCartItems.reduce(
          (acc, item) => acc + (item.price || 0),
          0
        );

        return {
          ...prevState,
          cart: {
            ...prevState.cart,
            cartItems: updatedCartItems,
            total: parseFloat(newTotal.toFixed(2)),
          },
          editedItems: {
            ...prevState.editedItems,
            [productId]: updatedItem,
          },
          // Cualquier edición invalida el recálculo previo
          recalculatedTotal: null,
          recalcDiscountTotal: null,
          recalcLines: [],
          recalcWarnings: [],
        };
      });
    }
  };

  const handleSaveChanges = async () => {
    setOrderState((prevState) => ({ ...prevState, updating: true }));
    try {
      const editedItemsArray = Object.values(orderState.editedItems);

      const updateProduct = async (item) => {
        const { custom_fields, name, ...restItem } = item;
        const isPieceKg =
          custom_fields &&
          custom_fields.tipo === 'Kg' &&
          !!custom_fields.pesokg;

        const updatedProduct = {
          ...restItem,
          custom_fields,
        };

        if (isPieceKg) {
          updatedProduct.custom_name = `Pieza(s) de ${name} por un total de ${restItem.selected_weight.toFixed(
            3
          )} kg`;
        }

        await backend.put(
          `/orders/${orderId}/products/${restItem.product_id}`,
          updatedProduct
        );
      };

      // Un fallo no debe cortar el resto ni pasar desapercibido: la BD del
      // carrito ya pudo cambiar aunque BC haya rechazado la línea.
      const failed = [];
      for (const item of editedItemsArray) {
        try {
          await updateProduct(item);
        } catch (error) {
          console.error(`Error updating product ${item.product_id}:`, error);
          failed.push(item.name);
        }
      }

      if (failed.length > 0) {
        alert(
          `No se pudieron guardar los cambios de: ${failed.join(', ')}. ` +
            'Revisa que el total de la orden coincida antes de cobrar.'
        );
      }
    } catch (error) {
      console.error('Error updating order products or total:', error);
    } finally {
      await fetchOrderDetails();
      setOrderState((prevState) => ({
        ...prevState,
        editedItems: {},
        editing: false,
        updating: false,
      }));
    }
  };

  const handleProductSelect = (e) => {
    const productId = !isNaN(e.target.value) ? +e.target.value : 0;
    const product = orderState.searchedProducts.find(
      (item) => item?.node?.entityId === productId
    );

    if (!product) {
      setOrderState((prevState) => ({
        ...prevState,
        selectedProduct: null,
      }));
      return;
    }

    setOrderState((prevState) => ({
      ...prevState,
      selectedProduct: product.node,
    }));
  };

  const handleAddProductToOrder = async () => {
    if (!orderState.selectedProduct)
      return alert('Intenta seleccionar un producto');

    const { entityId, sku, prices, name, selected_weight, customFields } =
      orderState.selectedProduct;
    const { price, salePrice } = prices || {};
    const productPrice = salePrice?.value || price?.value;
    const type = getProductType(customFields.edges);
    // Una línea en qty 0 es un producto quitado; se puede volver a agregar.
    const alreadyInOrder = !!orderState.order.products.find(
      ({ product_id, quantity }) =>
        product_id === entityId && Number(quantity) > 0
    );
    const isKg = type === PRODUCT_TYPES.KILOGRAM;
    const isPieceKg = type === PRODUCT_TYPES.PIECE_KILO;
    const quantity = isKg
        ? orderState.selectedProduct.quantity
        : Math.floor(orderState.selectedProduct.quantity);
    let amount = 0,
      custom_name = `Pieza(s) de ${name}`;

    if (alreadyInOrder)
      return alert('No se puede agregar un producto que ya esta en la orden');

    if (!quantity) return alert('Necesitas colocar una cantidad');

    if (isPieceKg && !selected_weight)
      return alert(
        'Necesitas especificar el peso seleccionado para este tipo de producto'
      );

    if (isKg) {
      amount = productPrice * quantity;
      custom_name = `1 Orden de ${quantity.toFixed(3)} kg de ${name}`;
    } else if (isPieceKg) {
      amount = (productPrice * selected_weight) / quantity;
      custom_name = `Pieza(s) de ${name} por un total de ${selected_weight.toFixed(3)} kg`
    } else {
      amount = productPrice;
    }

    const product = {
      product_id: entityId,
      quantity: isKg ? 1 : quantity,
      price_inc_tax: amount,
      price_ex_tax: amount,
      name: custom_name,
      name_customer: custom_name,
      name_merchant: custom_name,
    };

    // `product` solo se usa en el flujo solo-BC (ajustes ZAJU/envío u órdenes
    // sin carrito); en el resto el backend recalcula precio y nombre.
    const item = {
      product_id: entityId,
      sku,
      quantity,
      selected_weight: isPieceKg ? selected_weight : undefined,
    };

    setOrderState((prevState) => ({ ...prevState, isProcessing: true }));

    try {
      const { data } = await backend.post(`/add-to-order`, {
        product,
        item,
        orderId: orderState.order.id,
      });

      if (data?.warning) alert(data.warning);

      // El producto ahora vive en el carrito de DB; sin recargarlo no aparece en la tabla.
      if (orderState.order.cart_id) {
        const { data: cartData } = await backend.get(
          `/cart/${orderState.order.cart_id}`
        );
        setOrderState((prevState) => ({
          ...prevState,
          cart: cartData,
          originalCart: cartData,
        }));
      }
    } catch (error) {
      console.error('An error ocurred when add product to order:', error);
      alert(
        error.response?.data?.msg ||
          'No se pudo agregar el producto a la orden. Intenta de nuevo.'
      );
    } finally {
      await fetchOrderDetails();
      setOrderState((prevState) => ({
        ...prevState,
        isProcessing: false,
      }));
    }
  };

  const getProductVisualTotal = () => {
    if (!orderState.selectedProduct) return 0;

    const { prices, selected_weight, customFields } =
      orderState.selectedProduct;
    const { price, salePrice } = prices || {};
    const productPrice = salePrice?.value || price?.value;
    const type = getProductType(customFields.edges);
    const isKg = type === PRODUCT_TYPES.KILOGRAM;
    const isPieceKg = type === PRODUCT_TYPES.PIECE_KILO;
    const quantity = isKg
        ? +orderState.selectedProduct.quantity
        : Math.max(orderState.selectedProduct.quantity, 1);
    let amount = 0;

    if (isPieceKg) {
      amount = productPrice * selected_weight;
    } else {
      amount = productPrice * quantity;
    }

    return !isNaN(amount) ? amount : 0;
  };

  const handleEditToggle = () => {
    setOrderState((prevState) => ({
      ...prevState,
      editing: !prevState.editing,
    }));
  };

  const handleCancelEdit = () => {
    setOrderState((prevState) => ({
      ...prevState,
      cart: prevState.originalCart,
      editedItems: {},
      editing: false,
      // Volver a estado limpio invalida cualquier recálculo pendiente
      recalculatedTotal: null,
      recalcDiscountTotal: null,
      recalcLines: [],
      recalcWarnings: [],
    }));
  };

  const handleRecalculateTotal = async () => {
    if (!orderState.order) return;
    setOrderState((prevState) => ({ ...prevState, recalculating: true }));
    try {
      // Si hay ediciones pendientes, guardarlas primero — BC necesita ver el carrito final
      // antes de que podamos pedir un recálculo coherente.
      if (Object.keys(orderState.editedItems).length > 0) {
        await handleSaveChanges();
      }

      // Releer la orden para tener el snapshot de productos post-edición.
      const refreshed = await backend.get(`/orders/${orderId}`);
      const refreshedOrder = refreshed.data;

      // BC conserva la línea con quantity=0 cuando se "quita" un item; el recalc no
      // las acepta (zod positive). Filtramos para no romper la request.
      const items = (refreshedOrder.products || [])
        .filter((p) => Number(p.quantity) > 0)
        .map((p) => ({
          product_id: p.product_id,
          variant_id: p.variant_id > 0 ? p.variant_id : undefined,
          quantity: p.quantity,
          line_total: parseFloat(p.total_inc_tax),
        }));

      if (items.length === 0) {
        alert('La orden no tiene items con cantidad para recalcular.');
        setOrderState((prevState) => ({ ...prevState, recalculating: false }));
        return;
      }

      const shippingTotal = parseFloat(refreshedOrder.shipping_cost_inc_tax) || 0;

      const { data } = await backend.post(`/orders/${orderId}/recalculate`, {
        items,
        shipping_total: shippingTotal,
      });

      // El backend devuelve `coupons_breakdown` con el discount_amount
      // calculado por cupón. Lo mezclamos con orderState.coupons para que la
      // UI muestre el monto al lado de cada cupón sin depender de BC v2
      // (que en órdenes reales sí trae el dato, pero en testing manual no).
      const breakdown = Array.isArray(data?.coupons_breakdown)
        ? data.coupons_breakdown
        : [];

      setOrderState((prevState) => {
        const updatedCoupons = (prevState.coupons || []).map((c) => {
          const match = breakdown.find(
            (b) =>
              (b.coupon_id != null && b.coupon_id === c.coupon_id) ||
              (!!b.code && !!c.code && b.code === c.code)
          );
          // Tras un recálculo manual, el breakdown es la fuente más fresca:
          // siempre sobrescribimos `discount_amount` (no solo cuando es null).
          // Esto evita mostrar montos stale después de editar la orden.
          if (match) {
            return { ...c, discount_amount: match.discount_amount };
          }
          return c;
        });

        return {
          ...prevState,
          order: refreshedOrder,
          coupons: updatedCoupons,
          recalculatedTotal: data?.total_final ?? null,
          recalcDiscountTotal: data?.discount_total ?? null,
          recalcLines: Array.isArray(data?.lines) ? data.lines : [],
          recalcWarnings: Array.isArray(data?.warnings) ? data.warnings : [],
          recalculating: false,
        };
      });
    } catch (error) {
      console.error('Error recalculating order total:', error);
      alert(
        error.response?.data?.msg ||
          `No se pudo recalcular el total: ${error.message}`
      );
      setOrderState((prevState) => ({ ...prevState, recalculating: false }));
    }
  };

  const handleCaptureFunds = async () => {
    setOrderState((prevState) => ({ ...prevState, isProcessing: true }));
    try {
      // expectedTotal previene cobrar un monto distinto al que el operador validó.
      // - Con cupones: usar recalculatedTotal (canónico tras recálculo del Fastify).
      // - Sin cupones: usar total_inc_tax (BC sigue siendo fuente de verdad).
      const expectedTotal = hasCoupons
        ? orderState.recalculatedTotal
        : parseFloat(orderState.order?.total_inc_tax);

      const response = await backend.post(`/orders/${orderId}/capture-funds`, {
        customerId: orderState.order.customer_id,
        expectedTotal,
      });

      if (
        response.status === 200 &&
        orderState.shippingMethod.includes('Recoger en Sucursal')
      ) {
        await backend.put(`/orders/${orderId}`, { status_id: 8 });
      }

      await fetchOrderDetails();
    } catch (error) {
      console.error('Error capturing funds or updating order status:', error);
      alert(error.response?.data?.msg || `Error al capturar los fondos: ${error.message}`);
    } finally {
      setOrderState((prevState) => ({
        ...prevState,
        isProcessing: false,
        modals: { ...prevState.modals, confirm: false },
        editing: false,
      }));
    }
  };

  const handleConfirmPickup = async () => {
    setOrderState((prevState) => ({ ...prevState, isProcessing: true }));
    try {
      await backend.put(`/orders/${orderId}`, { status_id: 12 });
      await fetchOrderDetails();
    } catch (error) {
      console.error('Error updating order status:', error);
    } finally {
      setOrderState((prevState) => ({
        ...prevState,
        isProcessing: false,
        modals: { ...prevState.modals, confirmPickup: false },
        editing: false,
      }));
    }
  };

  const handleConfirmSendAndPayLater = async () => {
    setOrderState((prevState) => ({ ...prevState, isProcessing: true }));
    try {
      await backend.put(`/orders/${orderId}`, { status_id: 2 });
      await fetchOrderDetails();
    } catch (error) {
      console.error('Error updating order status:', error);
    } finally {
      setOrderState((prevState) => ({
        ...prevState,
        isProcessing: false,
        modals: { ...prevState.modals, confirmAndPayLater: false },
        editing: false,
      }));
    }
  };

  const handleBackClick = () => {
    if (location.state && location.state.from) {
      const cleanedPath = location.state.from
        .replace(/\s+/g, '-')
        .toLowerCase();
      navigate(`/${cleanedPath}`);
    } else {
      navigate('/orders');
    }
  };

  const canEdit = () => {
    return !!(
      orderState.order &&
      location.state?.from === 'preparando pedido' &&
      (!orderState.order.staff_notes ||
        !orderState.order.staff_notes.includes('Stripe Transaction ID')) &&
      orderState.order.status_id === 9
    );
  };

  const handleDownload = () => {
    const { order, cart, shippingMethod } = orderState;

    const generateFile = (content, fileName) => {
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
    };

    const formatCartItems = (withProductName = false, includeComments = false, separator = '\t', maxNameLength = 0) => {
      return cart.cartItems.map(item => {
        const product = order.products.find(p => p.product_id === item.product_id);
        const sku = product ? product.sku : 'SKU no encontrado';
        const price = formatCurrencyWithoutSymbol(item.non_sale_price);
        const quantity = item.quantity || item.selected_weight;
        let name = product?.name || '';
        if (maxNameLength && name.length > maxNameLength) {
          name = name.substring(0, maxNameLength) + '..';
        }
        const productName = withProductName ? `${separator}${name}` : '';
        const comment = includeComments && item.comment ? `${separator}${item.comment.trim()}` : '';
        return `${sku}${productName}${separator}${price}${separator}${quantity}${comment}\r\n`;
      }).join('');
    };

    // Generate first file content
    const content1 = [
      `${order.billing_address.last_name}\r\n`,
      formatCartItems(false, false)
    ].join('');

    generateFile(content1, `orden_${order.id}.txt`);

    // Generate second file content
    const content2 = [
      `APP-ORDEN: ${order.id}\r\n`,
      `Sucursal asignada: ${order.subsidiary ? order.subsidiary.name : 'N/A'}\r\n`,
      `Método de Pago: ${order.payment_method}\r\n`,
      `Fecha solicitada de entrega: ${extractScheduledDate(order.customer_message)}\r\n`,
      `Método de envío: ${shippingMethod}\r\n`,
      `Comentario de pedido: ${removeScheduledDate(order.customer_message)}\r\n`,
      `${order.billing_address.first_name}\r\n`,
      formatCartItems(true, true, '    ', 50)
    ].join('');

    generateFile(content2, `orden_${order.id}_C.txt`);
  };

  const sortItemsBySku = (cartItems) => {
    const { order } = orderState;
    if (!order?.products || !cartItems) return cartItems;

    const getCategory = (sku) => {
      if (/^\d+$/.test(sku)) return 1;
      if (/^[A-Za-z0-9]+$/.test(sku) && !/^[A-Za-z]+$/.test(sku)) return 2;
      if (/^[A-Za-z]+$/.test(sku)) return 3;
      return 4;
    };

    const compareSku = (a, b) => {
      const prodA = order.products.find((p) => p.product_id === a.product_id);
      const prodB = order.products.find((p) => p.product_id === b.product_id);
      const skuA = prodA?.sku || '';
      const skuB = prodB?.sku || '';
      const catA = getCategory(skuA);
      const catB = getCategory(skuB);

      if (catA !== catB) return catA - catB;
      if (catA === 1) return parseInt(skuA, 10) - parseInt(skuB, 10);
      return skuA.localeCompare(skuB);
    };

    return [...cartItems].sort(compareSku);
  };

  const sortedCartItems = orderState.cart?.cartItems
    ? sortItemsBySku(orderState.cart.cartItems)
    : [];

  const orderProductsAddedAfter = orderState.order?.products.filter(
    ({ product_id }) =>
      !orderState.cart?.cartItems.find(
        (item) => item.product_id === product_id
      )
  );

  return {
    canCaptureFunds,
    canConfirmPickup,
    canConfirmSendAndPayLater,
    canEdit,
    closeAddProductModal,
    closeConfirmAndPayLaterModal,
    closeConfirmModal,
    closeConfirmPickupModal,
    getProductVisualTotal,
    handleAddProductToOrder,
    handleBackClick,
    handleCancelEdit,
    handleCaptureFunds,
    handleConfirmPickup,
    handleConfirmSendAndPayLater,
    handleDownload,
    handleEditToggle,
    handleProductSelect,
    handleQuantityChange,
    handleQuantityValue,
    handleRecalculateTotal,
    handleSaveChanges,
    handleSearchValue,
    handleSelectedWeightValue,
    hasCoupons,
    hasCouponsForActions,
    isDirty,
    isPreparingOrder,
    modals,
    openAddProductModal,
    openConfirmAndPayLaterModal,
    openConfirmModal,
    openConfirmPickupModal,
    orderProductsAddedAfter,
    orderState,
    requiresRecalculation,
    requiresSaveBeforeCharge,
    sortedCartItems,
  };
};

export default useOrderDetails;
